import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  HStack,
  VStack,
  Input,
  Text,
  Checkbox,
  Box,
  useToast,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  useClipboard,
  Code,
  Tooltip,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
} from "@chakra-ui/react";
import { LockIcon } from "@chakra-ui/icons";
import {
  createWebhookEndpoint,
  updateWebhookEndpoint,
  getWebhookEventTypes,
} from "./api";
import { WebhookEventType, WebhookEndpointWithSecret, WebhookEndpoint } from "./types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  endpoint?: WebhookEndpoint;
}

export const CreateWebhookModal = ({
  isOpen,
  onClose,
  onCreated,
  endpoint: editEndpoint,
}: Props) => {
  const isEditMode = !!editEndpoint;
  const toast = useToast();
  const [url, setUrl] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [selectedEvents, setSelectedEvents] = React.useState<string[]>([]);
  const [eventTypes, setEventTypes] = React.useState<WebhookEventType[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [createdEndpoint, setCreatedEndpoint] =
    React.useState<WebhookEndpointWithSecret | null>(null);
  const { onCopy, hasCopied, setValue: setClipboardValue } = useClipboard("");

  const labelColor = useColorModeValue("gray.600", "gray.400");
  const secretBg = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const dividerColor = useColorModeValue("gray.100", "whiteAlpha.100");

  const groupedEvents = React.useMemo(() => {
    return eventTypes.reduce((groups, et) => {
      const sport = et.sport;
      if (!groups[sport]) groups[sport] = [];
      groups[sport].push(et);
      return groups;
    }, {} as Record<string, WebhookEventType[]>);
  }, [eventTypes]);

  const allAvailableEvents = React.useMemo(
    () => eventTypes.filter(e => e.available),
    [eventTypes]
  );
  const allAvailableSelected = allAvailableEvents.length > 0 && allAvailableEvents.every(e => selectedEvents.includes(e.type));

  React.useEffect(() => {
    if (isOpen) {
      getWebhookEventTypes()
        .then(setEventTypes)
        .catch(() => {});
      if (editEndpoint) {
        setUrl(editEndpoint.url);
        setDescription(editEndpoint.description || "");
        setSelectedEvents([...editEndpoint.event_types]);
      } else {
        setUrl("");
        setDescription("");
        setSelectedEvents([]);
      }
    }
  }, [isOpen, editEndpoint]);

  const handleSubmit = async () => {
    if (!url) {
      toast({
        title: "URL is required",
        status: "error",
        isClosable: true,
      });
      return;
    }
    if (selectedEvents.length === 0) {
      toast({
        title: "Select at least one event type",
        status: "error",
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      if (isEditMode) {
        await updateWebhookEndpoint(editEndpoint!.id, {
          url,
          description: description || undefined,
          event_types: selectedEvents,
        });
        toast({
          title: "Webhook endpoint updated",
          status: "success",
          isClosable: true,
          duration: 3000,
        });
        onCreated();
        handleClose();
      } else {
        const endpoint = await createWebhookEndpoint({
          url,
          description: description || undefined,
          event_types: selectedEvents,
        });
        setCreatedEndpoint(endpoint);
        setClipboardValue(endpoint.secret);
      }
    } catch (e: any) {
      const msg =
        e.response?.data?.error || (isEditMode ? "Failed to update webhook endpoint" : "Failed to create webhook endpoint");
      toast({
        title: "Error",
        description: msg,
        status: "error",
        isClosable: true,
      });
    }
    setIsLoading(false);
  };

  const handleClose = () => {
    setUrl("");
    setDescription("");
    setSelectedEvents([]);
    setCreatedEndpoint(null);
    onClose();
    if (createdEndpoint) {
      onCreated();
    }
  };

  // After creation: show the signing secret
  if (createdEndpoint) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="lg">
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent borderRadius="xl" boxShadow="2xl">
          <ModalHeader>Webhook Created</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Your webhook endpoint has been created. Copy your signing secret
                below — it will not be shown again.
              </Text>
              <Box>
                <Text fontSize="sm" fontWeight="500" color={labelColor} mb={1}>
                  Signing Secret
                </Text>
                <InputGroup>
                  <Input
                    value={createdEndpoint.secret}
                    isReadOnly
                    fontFamily="mono"
                    fontSize="sm"
                    bg={secretBg}
                  />
                  <InputRightElement width="4.5rem">
                    <Button
                      h="1.75rem"
                      size="sm"
                      onClick={onCopy}
                      colorScheme={hasCopied ? "green" : "brand"}
                    >
                      {hasCopied ? "Copied" : "Copy"}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </Box>
              <Text fontSize="sm" color="gray.500">
                Verifying webhook signatures is optional but recommended. Use
                this secret with the{" "}
                <Code fontSize="xs">X-BDL-Webhook-Signature</Code> header to
                validate payloads.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="brand" onClick={handleClose}>
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius="xl" boxShadow="2xl">
        <ModalHeader>{isEditMode ? "Edit Webhook Endpoint" : "Create Webhook Endpoint"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text fontSize="sm" fontWeight="500" color={labelColor} mb={1}>
                URL
              </Text>
              <Input
                placeholder="https://example.com/webhook"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="500" color={labelColor} mb={1}>
                Description (optional)
              </Text>
              <Input
                placeholder="My webhook"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Box>

            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" fontWeight="500" color={labelColor}>
                  Event Types
                </Text>
                {allAvailableEvents.length > 0 && (
                  <Checkbox
                    size="sm"
                    isChecked={allAvailableSelected}
                    isIndeterminate={selectedEvents.length > 0 && !allAvailableSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedEvents(allAvailableEvents.map(et => et.type));
                      } else {
                        setSelectedEvents([]);
                      }
                    }}
                  >
                    <Text fontSize="xs" color="gray.500">Select all</Text>
                  </Checkbox>
                )}
              </HStack>
              <Box maxH="320px" overflowY="auto">
                <Accordion allowMultiple aria-label="Webhook event types by sport">
                  {Object.entries(groupedEvents).map(([sport, events]) => {
                    const availableEvents = events.filter(e => e.available);
                    const selectedInGroup = events.filter(e => selectedEvents.includes(e.type));
                    const groupAllSelected = availableEvents.length > 0 && availableEvents.every(e => selectedEvents.includes(e.type));
                    const selectedCount = selectedInGroup.length;

                    return (
                      <AccordionItem
                        key={sport}
                        border="none"
                        borderBottom="1px solid"
                        borderColor={dividerColor}
                        _last={{ borderBottom: "none" }}
                      >
                        <AccordionButton
                          px={2}
                          py={2}
                          borderRadius="md"
                          _hover={{ bg: hoverBg }}
                        >
                          <HStack flex="1" spacing={2}>
                            <Text fontSize="sm" fontWeight="600" textTransform="uppercase">
                              {sport}
                            </Text>
                            {selectedCount > 0 && (
                              <Badge
                                colorScheme="brand"
                                variant="subtle"
                                fontSize="xs"
                                borderRadius="full"
                                px={2}
                                aria-label={`${selectedCount} of ${availableEvents.length} events selected`}
                              >
                                {selectedCount}/{availableEvents.length}
                              </Badge>
                            )}
                          </HStack>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel px={2} pb={3} pt={1}>
                          {availableEvents.length > 0 && (
                            <Checkbox
                              size="sm"
                              mb={2}
                              isChecked={groupAllSelected}
                              isIndeterminate={selectedInGroup.length > 0 && !groupAllSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const newEvents = Array.from(new Set([...selectedEvents, ...availableEvents.map(ev => ev.type)]));
                                  setSelectedEvents(newEvents);
                                } else {
                                  setSelectedEvents(selectedEvents.filter(se => !events.some(ev => ev.type === se)));
                                }
                              }}
                            >
                              <Text fontSize="xs" color="gray.500">Select all</Text>
                            </Checkbox>
                          )}
                          <VStack align="stretch" spacing={1} pl={2}>
                            {events.map((et) => (
                              <Checkbox
                                key={et.type}
                                isChecked={selectedEvents.includes(et.type)}
                                isDisabled={!et.available}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedEvents([...selectedEvents, et.type]);
                                  } else {
                                    setSelectedEvents(selectedEvents.filter(s => s !== et.type));
                                  }
                                }}
                              >
                                <HStack spacing={2}>
                                  <Text fontSize="sm" fontFamily="mono">{et.type}</Text>
                                  <Text fontSize="xs" color="gray.500">
                                    &mdash; {et.description}
                                  </Text>
                                  {!et.available && (
                                    <Tooltip
                                      label="Upgrade to ALL-ACCESS to unlock"
                                      hasArrow
                                      fontSize="xs"
                                      placement="top"
                                    >
                                      <LockIcon boxSize={3} color="gray.500" />
                                    </Tooltip>
                                  )}
                                </HStack>
                              </Checkbox>
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </Box>
              {selectedEvents.length > 0 && (
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {selectedEvents.length} event{selectedEvents.length !== 1 ? "s" : ""} selected
                </Text>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack justify="space-between" w="100%">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              isLoading={isLoading}
              onClick={handleSubmit}
            >
              {isEditMode ? "Save Changes" : "Create"}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
